import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import MentionInput from '@/components/MentionInput';
import { convertMentionsForStorage, renderMentions, getUniqueMentions } from '@/lib/mentionUtils';
import { sendNotification } from '@/lib/notifications';
import {
  Send,
  MessageSquare,
  User
} from 'lucide-react';

/**
 * TaskComments Component
 * Reusable comment section for tasks
 *
 * @param {Object} props
 * @param {string} props.taskId - The task_id to fetch/post comments for
 * @param {string} props.taskName - The task name for notification context
 * @param {string} props.projectId - The project_id for notification links
 * @param {string} props.className - Optional additional CSS classes
 */
const TaskComments = ({ taskId, taskName, projectId, className = '' }) => {
  const { userProfile } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionMap, setMentionMap] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const mentionInputRef = React.useRef(null);

  // Fetch comments when taskId changes
  useEffect(() => {
    if (taskId) {
      fetchComments();
    }
  }, [taskId]);

  const fetchComments = async () => {
    if (!taskId) return;

    try {
      setLoadingComments(true);
      const { data, error } = await supabase
        .from('task_comments')
        .select(`
          *,
          user_profiles (
            id,
            first_name,
            last_name,
            display_name,
            avatar_url,
            email
          )
        `)
        .eq('task_id', taskId)
        .order('created_date', { ascending: true });

      if (error) throw error;

      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (!userProfile?.id) {
      toast.error('You must be logged in to comment');
      return;
    }

    try {
      setSubmittingComment(true);

      // Convert mentions to storage format with UUIDs
      const commentWithUUIDs = convertMentionsForStorage(newComment.trim(), mentionMap);

      // Extract unique mentioned user IDs from the final comment text
      const finalMentionedUserIds = getUniqueMentions(commentWithUUIDs);

      // Save comment to database
      const { data: insertedComment, error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          user_id: userProfile.id,
          comment: commentWithUUIDs
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Comment added');

      // Send notifications to mentioned users (excluding self)
      if (finalMentionedUserIds.length > 0) {
        const usersToNotify = finalMentionedUserIds.filter(id => id !== userProfile.id);

        if (usersToNotify.length > 0) {
          // Construct link URL
          const linkUrl = `/space/${projectId}/tasks?view=${taskId}`;

          // Send notifications
          await sendNotification({
            notification_type: 'mentions',
            user_ids: usersToNotify,
            title: 'You were mentioned',
            message: `${userProfile.display_name || userProfile.first_name} mentioned you in a comment on task: ${taskName}`,
            link_url: linkUrl,
            entity_type: 'task_comment',
            entity_id: insertedComment.id,
            actor_id: userProfile.id,
            space_id: projectId || null,
            group_key: `task_${taskId}_mentions`
          });
        }
      }

      // Clear input
      setNewComment('');
      setMentionedUserIds([]);
      setMentionMap({});

      // Clear the contentEditable div
      if (mentionInputRef.current) {
        mentionInputRef.current.clear();
      }

      // Refresh comments to show the new one
      await fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatCommentDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-900">Activity</h3>

      {/* Comments List */}
      {loadingComments ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-400 animate-pulse" />
          <p className="text-sm">Loading comments...</p>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              {comment.user_profiles?.avatar_url ? (
                <img
                  src={comment.user_profiles.avatar_url}
                  alt={comment.user_profiles.display_name}
                  className="h-8 w-8 rounded-full flex-shrink-0"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-purple-600" />
                </div>
              )}
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm">
                    {comment.user_profiles?.display_name
                      || `${comment.user_profiles?.first_name || ''} ${comment.user_profiles?.last_name || ''}`.trim()
                      || comment.user_profiles?.email
                      || 'Unknown User'}
                  </p>
                  <span className="text-xs text-gray-500">
                    {formatCommentDate(comment.created_date)}
                  </span>
                  {comment.edited && (
                    <Badge variant="outline" className="text-xs">
                      Edited
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {renderMentions(comment.comment).map((part, idx) => {
                    if (typeof part === 'string') {
                      return <span key={idx}>{part}</span>;
                    } else if (part.type === 'mention') {
                      return (
                        <span
                          key={part.key}
                          className="font-semibold text-blue-600 bg-blue-50 px-1 rounded"
                        >
                          @{part.displayName}
                        </span>
                      );
                    }
                    return null;
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No comments yet. Be the first to add one!</p>
        </div>
      )}

      {/* Add Comment Form */}
      <div className="mt-6">
        <div className="flex items-start space-x-4">
          {/* User Avatar */}
          {userProfile?.avatar_url ? (
            <img
              src={userProfile.avatar_url}
              alt={userProfile.display_name}
              className="h-10 w-10 rounded-full flex-shrink-0"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-purple-600" />
            </div>
          )}

          {/* Input Container */}
          <div className="flex-1 min-w-0">
            <MentionInput
              ref={mentionInputRef}
              value={newComment}
              onChange={(text, mentionedIds, mentionMapData) => {
                setNewComment(text);
                setMentionedUserIds(mentionedIds);
                setMentionMap(mentionMapData);
              }}
              placeholder="Add a comment..."
              rows={3}
              className="w-full"
              disabled={submittingComment}
              onKeyDown={(e) => {
                // Submit on Ctrl+Enter or Cmd+Enter
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
            />
            <div className="mt-3 flex justify-end">
              <Button
                onClick={handleAddComment}
                disabled={submittingComment || !newComment.trim()}
                size="sm"
              >
                <Send className="h-4 w-4 mr-2" />
                {submittingComment ? 'Posting...' : 'Post Comment'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskComments;
